import React from 'react';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

interface PricingCardProps {
  Icon: React.ElementType;
  title: string;
  number: number;
  text: string;
  delay: number;
}

const PricingCard: React.FC<PricingCardProps> = ({ Icon, title, number, text, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ 
        y: -10,
        transition: { duration: 0.3 }
      }}
      className='group relative w-full bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 mt-6'
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50/50 to-[#f84831]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
      
      {/* Number Badge */}
      <div className="absolute -top-6 left-8 z-10">
        <div className="w-12 h-12 bg-gradient-to-r from-[#f84831] to-[#ff6b54] rounded-xl flex items-center justify-center shadow-lg">
          <span className="text-white font-bold text-lg">{number}</span>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="relative p-8 pt-8">

        {/* Icon */}
        <div className='w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#f84831]/10 to-[#f84831]/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
          <Icon style={{ fontSize: '2rem', color: '#f84831' }} />
        </div>

        {/* Title */}
        <h3 className='text-2xl font-bold text-gray-800 text-center mb-4 group-hover:text-[#f84831] transition-colors duration-300'>
          {title}
        </h3>

        {/* Description */}
        <p className='text-gray-600 text-center leading-relaxed mb-6'>
          {text}
        </p>

        {/* Arrow Indicator */}
        <div className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-8 h-8 bg-[#f84831] rounded-full flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>

      {/* Bottom Accent Line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#f84831] to-[#ff6b54] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
    </motion.div>
  );
};

const Works: React.FC = () => {
  const { t } = useTranslation();

  const workInfoData = [
    {
      Icon: PermPhoneMsgIcon,
      title: t("functionality.parts.first.title"),
      text: t("functionality.parts.first.text"),
    },
    {
      Icon: PeopleAltIcon,
      title: t("functionality.parts.second.title"),
      text: t("functionality.parts.second.text"),
    },
    {
      Icon: DoneAllIcon,
      title: t("functionality.parts.third.title"),
      text: t("functionality.parts.third.text"),
    },
  ];

  return (
    <div className='w-full py-16 md:py-24 px-4 bg-gradient-to-br from-white via-gray-50 to-white'>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className='text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-[#f84831] via-[#ff6b54] to-[#f84831] bg-clip-text text-transparent leading-tight'>
            {t('functionality.title')}
          </h2>
          <div className="mx-auto h-1 w-16 md:w-24 bg-gradient-to-r from-[#f84831] to-[#ff6b54] mb-4 md:mb-6 rounded-full"></div>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
            {t('network.processDescription')}
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10 relative pt-6'>
          {workInfoData.map((item, index) => (
            <PricingCard 
              key={index} 
              number={index + 1} 
              Icon={item.Icon} 
              title={item.title} 
              text={item.text}
              delay={index * 0.2}
            />
          ))}

          {/* Process Flow Arrows - Hidden on mobile */}
          <div className="hidden md:flex absolute top-1/2 left-0 right-0 transform -translate-y-1/2 pointer-events-none z-20">
            <div className="flex justify-between items-center w-full px-8 lg:px-16">
              <div className="flex-1"></div>
              <motion.div 
                className="w-12 lg:w-16 h-0.5 bg-gradient-to-r from-[#f84831]/40 to-[#f84831]/60 mx-4"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
              ></motion.div>
              <div className="flex-1"></div>
              <motion.div 
                className="w-12 lg:w-16 h-0.5 bg-gradient-to-r from-[#f84831]/40 to-[#f84831]/60 mx-4"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.8 }}
              ></motion.div>
              <div className="flex-1"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Works;
